import React, { useEffect, useState } from 'react';
import { leadsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, PageHeader, Badge, Button, Modal, FormField, Table, Empty, Alert, FilterPills, Loading, SearchInput } from '../components/ui';
import { Plus, Pencil, Trash2 } from 'lucide-react';

const empty = { name: '', email: '', phone: '', status: 'New', source: 'Other' };

export default function Leads() {
  const { canCreate, canUpdate, canDelete } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = () => { setLoading(true); leadsAPI.list().then(r => setLeads(r.data.data || [])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setError(''); setModal(true); };
  const openEdit = (l) => { setEditing(l); setForm({ name: l.name, email: l.email||'', phone: l.phone||'', status: l.status, source: l.source }); setError(''); setModal(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (editing) await leadsAPI.update(editing.lead_id, form);
      else await leadsAPI.create(form);
      setModal(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    await leadsAPI.delete(id); load();
  };

  const filtered = leads.filter(l => {
    let match = true;
    if (filter) match = l.status === filter;
    if (search) {
      const lower = search.toLowerCase();
      match = match && (l.name?.toLowerCase().includes(lower) || l.email?.toLowerCase().includes(lower));
    }
    return match;
  });

  const columns = [
    { key: 'name', label: 'Name', render: r => (
      <div>
        <div style={{ fontWeight: 600 }}>{r.name}</div>
        {r.email && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.email}</div>}
      </div>
    )},
    { key: 'phone', label: 'Phone', render: r => r.phone || '—' },
    { key: 'source', label: 'Source', render: r => <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{r.source}</span> },
    { key: 'status', label: 'Status', render: r => <Badge value={r.status} /> },
    { key: 'createdAt', label: 'Created', render: r => new Date(r.createdAt).toLocaleDateString() },
    { key: 'actions', label: '', render: r => (canUpdate || canDelete) ? (
      <div style={{ display: 'flex', gap: 8 }}>
        {canUpdate && <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openEdit(r); }}><Pencil size={13} /></Button>}
        {canDelete && <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); handleDelete(r.lead_id); }}><Trash2 size={13} /></Button>}
      </div>
    ) : null}
  ];

  return (
    <div className="animate-in">
      <PageHeader
        title="Leads"
        subtitle={`${filtered.length} leads`}
        eyebrow="CRM"
        action={
          <div style={{ display: 'flex', gap: 12 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search leads..." />
            {canCreate && (
              <Button onClick={openCreate}><Plus size={15} />New Lead</Button>
            )}
          </div>
        }
      />

      <FilterPills
        options={[
          { value: '', label: 'All' },
          { value: 'New', label: 'New' },
          { value: 'Contacted', label: 'Contacted' },
          { value: 'Qualified', label: 'Qualified' },
          { value: 'Unqualified', label: 'Unqualified' },
          { value: 'Converted', label: 'Converted' },
        ]}
        value={filter}
        onChange={setFilter}
      />

      <Card>
        {loading ? <Loading />
          : filtered.length === 0 ? <Empty message="No leads found" />
          : <Table columns={columns} data={filtered} />}
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Edit Lead' : 'New Lead'}>
        <Alert type="error" message={error} />
        <form onSubmit={handleSave}>
          <FormField label="Name"><input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required placeholder="Lead name" /></FormField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FormField label="Email"><input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="email@example.com" /></FormField>
            <FormField label="Phone"><input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="+1 234 567 8900" /></FormField>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FormField label="Status">
              <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))}>
                {['New','Contacted','Qualified','Unqualified','Converted'].map(s => <option key={s}>{s}</option>)}
              </select>
            </FormField>
            <FormField label="Source">
              <select value={form.source} onChange={e => setForm(f => ({...f, source: e.target.value}))}>
                {['Website','Referral','Social Media','Email','Cold Call','Other'].map(s => <option key={s}>{s}</option>)}
              </select>
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button variant="secondary" onClick={() => setModal(false)} type="button">Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
